<template>
  <ApplicationAddable
    v-bind="$attrs"
    :items="items"
    :item-init="$attrs.itemInit"
    :text-item="$attrs.applicationType != 'owner-occupancy' ? translate('hakija') : translate('varaaja')"
    :min="Object.keys(existingApplicantIndexes).length >= 2 ? 2 : 1"
    :max="4"
    @update="$emit('update', $event)"
    :interactive="!preview"
  >
    <template #title="{index, total}" v-if="$attrs.applicationType != 'owner-occupancy'">
      {{sprintf(translate('Hakijan %s tiedot'), indexString(index, total))}}
    </template>
    <template #title="{ index, total }" v-if="$attrs.applicationType == 'owner-occupancy'">
        {{ sprintf(translate('Varaajan %s tiedot'), indexString(index, total)) }}
    </template>

    <template #title-description v-if="$attrs.applicationType != 'owner-occupancy'">
      {{translate('Täytä vähintään vaaditut kentät asianmukaisin tiedoin, jotta voimme käsitellä hakemuksesi'
      + ' mahdollisimman pikaisesti.')}}
    </template>
    <template #title-description v-if="$attrs.applicationType == 'owner-occupancy'">
      {{translate('Täytä vähintään vaaditut kentät asianmukaisin tiedoin, jotta voimme käsitellä varauksesi'
      + ' mahdollisimman pikaisesti.')}}
    </template>

    <template #default="{index: i, item}">
      <!-- Guard against vIter[i] not being ready (v$ initialization timing) -->
      <div v-if="!vIter[i] && !vIter[String(i)]" class="form form--bg-lighter-green">
        <div class="form__title-wrapper">
          <div class="form__title">
            <p>{{ translate('Ladataan lomaketta...') }}</p>
          </div>
        </div>
      </div>

      <!-- Render applicants section when vuelidate is ready -->
      <div v-else class="form form--bg-lighter-green">

        <div class="form__title-wrapper">
          <div class="form__title">
              <h2 class="h3">
                {{translate('Hakijan tiedot')}}
              </h2>
          </div>
        </div>

        <!-- Render applicants form -->
        <div class="form__grid">

          <div class="form__row">
            <InputWrap
              class="form__col-4"
              data-cy="first-name"
              :label="translate('Etunimet')"
              :validations="safeVIter(i).FirstName"
              v-slot="{error, touch}"
            >
              <InputText
                v-model="item.FirstName"
                data-cy="first-name-input"
                :disabled="preview"
                :error="error"
                @blur="touch"
              />
            </InputWrap>

            <InputWrap
              class="form__col-4"
              data-cy="last-name"
              :label="translate('Sukunimi')"
              :validations="safeVIter(i).LastName"
              v-slot="{error, touch}"
            >
              <InputText
                v-model="item.LastName"
                data-cy="last-name-input"
                :disabled="preview"
                :error="error"
                @blur="touch"
              />
            </InputWrap>

            <InputWrap
              class="form__col-4"
              data-cy="ssn"
              :label="translate('Henkilötunnus')"
              :validations="safeVIter(i).Ssn"
              :info="ssnDisabled(i)
                ? ($attrs.applicationType != 'owner-occupancy' ? translate('Hakemuksen päähakijaa ei voi muuttaa.') : translate('Varauksen päähakijaa ei voi muuttaa'))
                : null"
              v-slot="{error, touch}"
            >
              <InputText
                v-if="!ssnDisabled(i)"
                v-model="item.Ssn"
                data-cy="ssn-input"
                :disabled="preview || ssnDisabled(i)"
                :error="error"
                @blur="touch"
              />
              <span class="input-text" data-cy="ssn-input" v-else>
                <input class="input-text__input" autocomplete="off" type="hidden" data-cy="ssn-input" :value="item.Ssn" />
                <!---->
                <span class="input-text__input" style="">{{ item.Ssn.substring(0, item.Ssn.length - 4) + '****'}}</span>
              </span>

            </InputWrap>

          </div>

          <div
            v-if="!item.usePrimaryApplicantAddress"
            class="form__row"
          >
            <InputWrap
              class="form__col-6"
              data-cy="street-address"
              :label="translate('Lähiosoite')"
              :validations="safeVIter(i).Address"
              v-slot="{error, touch}"
            >
              <InputText
                v-model="item.Address"
                data-cy="street-address-input"
                :disabled="preview"
                :error="error"
                @blur="touch"
              />
            </InputWrap>

            <InputWrap
              class="form__col-3"
              data-cy="postal-code"
              :label="translate('Postinumero')"
              :validations="safeVIter(i).PostalCode"
              v-slot="{error, touch}"
            >
              <InputText
                v-model="item.PostalCode"
                data-cy="postal-code-input"
                :disabled="preview"
                :error="error"
                @blur="touch"
              />
            </InputWrap>

            <InputWrap
              class="form__col-3"
              data-cy="post-office"
              :label="translate('Postitoimipaikka')"
              :validations="safeVIter(i).City"
              v-slot="{error, touch}"
            >
              <InputText
                v-model="item.City"
                data-cy="post-office-input"
                :disabled="preview"
                :error="error"
                @blur="touch"
              />
            </InputWrap>
          </div>

          <div
            v-if="i > 0"
            class="form__row"
          >
            <div class="form__col">
              <InputCheckbox
                v-model="item.usePrimaryApplicantAddress"
                :disabled="preview"
                wrapperClasses="input-wrapper input-wrapper--flex"
              >
                {{translate('Osoite sama kuin päähakijalla')}}
              </InputCheckbox>
            </div>
          </div>

          <div class="form__row">
            <InputWrap
              class="form__col"
              data-cy="contact-language"
              :label="translate('Asiointikieli')"
              :wrap-label="false"
              v-slot="{error, touch}"
              :validations="safeVIter(i).ContactLanguage"
            >
              <InputSelect
                v-model="item.ContactLanguage"
                :error="error"
                :allowEmpty="false"
                :value="[
                  {label: 'Suomi', value: 'fi'}, // Finland
                ]"
                :options="[
                  {label: 'Suomi', value: 'fi'}, // Finland
                  {label: 'Ruotsi', value: 'sv'}, // Sweden
                  {label: 'Englanti', value: 'en'}, // English
                ]"
                simple-value
                @close="touch"
              />
            </InputWrap>
          </div>


          <div class="form__row">
            <div
              v-if="!preview && $attrs.applicationType != 'owner-occupancy'"
              class="form__col print-hide"
            >
                {{ translate('Tarvitsemme hakemustanne varten puhelinnumeron tai sähköpostiosoitteen. Voit myös antaa'
                  + ' molemmat.')}}
            </div>
            <div
              v-if="!preview && $attrs.applicationType == 'owner-occupancy'"
              class="form__col print-hide"
            >
                {{ translate('Tarvitsemme varaustanne varten puhelinnumeron tai sähköpostiosoitteen. Voit myös antaa'
                  + ' molemmat.')}}
            </div>

            <InputWrap
              class="form__col-3"
              data-cy="phone-number-country-code"
              :label="translate('Maakoodi')"
              :wrap-label="false"
              v-slot="{error, touch}"
              :validations="safeVIter(i).PhoneCountryCode"
            >
              <InputSelect
                v-model="item.PhoneCountryCode"
                :error="error"
                :allowEmpty="false"
                :value="[
                    {label: '+358', value: '+358'}, // Finland
                ]"
                :options="[
                  {label: '+358', value: '+358'}, // Finland
                  {label: '+372', value: '+372'}, // Estonia
                  {label: '+46', value: '+46'}, // Sweden
                  {label: '+44', value: '+44'}, // UK
                  {label: '+49', value: '+49'}, // Germany
                  {label: '+1', value: '+1'}, // USA
                  {label: '-----------', value: '', $isDisabled: true}, // Disabled divider
                  ...countryCodes // All country codes
                ]"
                simple-value
                @close="touch"
              />
            </InputWrap>

            <InputWrap
              class="form__col-6"
              data-cy="phone-number"
              :label="translate('Puhelinnumero')"
              :validations="safeVIter(i).Phone"
              :validation-messages="{required: 'Puhelinnumero vaaditaan.'}"
              v-slot="{error, touch}"
            >
              <InputText
                v-model="item.Phone"
                data-cy="phone-number-input"
                :disabled="preview"
                :error="error"
                @blur="touch"
              />
              <div style="font-size: 14px; line-height: 15px; margin-top: 6px;">
                <span style="user-select: none; border: 1px solid orange; border-radius: 50%; width: 20px; height: 20px; display: inline-block; text-align: center; line-height: 20px; font-size: 14px; margin-right: 4px; font-weight: bold; position: relative; top: 1px;">!</span> {{ translate("Syötä puhelinnumero muodossa 0401234567") }}
              </div>
            </InputWrap>
          </div>

          <div class="form__row">
            <InputWrap
              class="form__col-6"
              data-cy="email"
              :label="translate('Sähköposti')"
              :validations="safeVIter(i).Email"
              v-slot="{error, touch}"
            >
              <InputText
                v-model="item.Email"
                data-cy="email-input"
                type="email"
                :disabled="preview"
                :error="error"
                @blur="touch"
              />
            </InputWrap>

            <InputWrap
              v-if="!preview"
              class="form__col-6 print-hide"
              data-cy="verify-email"
              :label="translate('Sähköposti uudelleen')"
              :validations="safeVIter(i).emailAgain"
              v-slot="{error, touch}"
            >
              <InputText
                v-model="item.emailAgain"
                data-cy="verify-email-input"
                type="email"
                :disabled="preview"
                :error="error"
                @blur="touch"
              />
            </InputWrap>
          </div>

          <slot :vApplicant="safeVIter(i)" :item="item" :index="i"/>

          <div
            v-if="i === 0"
            class="form__row"
          >
            <div class="form__col">
              <InputCheckbox
                wrapperClasses="input-wrapper input-wrapper--flex"
                v-model="item.MarketingAllowed"
                data-cy="marketing-allowed"
                :disabled="preview"
              >
                {{translate('Haluan tilata Avain Asuntojen uutiskirjeen ja saada sähköpostiini tietoa asunnoista,'
                + ' palveluista ja tapahtumista. (Tilauksen voi peruuttaa koska tahansa uutiskirjeen linkistä.)')}}
              </InputCheckbox>
            </div>
          </div>

        </div>
      </div>
    </template>
  </ApplicationAddable>
</template>

<script>
  import {sprintf} from 'sprintf-js';

  import { countryCodes } from "../../helpers/countries"
  import vueBind from '../../lib/vueBind';
  import utils from '../mixins/utils';
  import ApplicationAddable from './ApplicationAddable';
  import InputWrap from './InputWrap';
  import InputText from './InputText';
  import InputCheckbox from './InputCheckbox';
  import InputSelect from './InputSelect.vue';

  export default {
    components: {
      ApplicationAddable,
      InputWrap,
      InputText,
      InputCheckbox,
      InputSelect,
    },
    mixins: [
      utils.translate,
      utils.safeVIter,
    ],
    data() {
      return {
        countryCodes,
      }
    },
    props: {
      items: {
        type: Array,
        required: false,
        default: () => [],
      },
      vIter: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      preview: {
        type: Boolean,
        required: true,
      },
      isExistingApplication: {
        type: Boolean,
        required: true,
      },
      existingApplicantIndexes: {
        type: Object,
        required: true,
        default() { return {} },
      },
      /** How many applicants existed when state was loaded; indices >= this are user-added (checksum / CRM). */
      initialApplicantsCount: {
        type: Number,
        default: 1,
      },
    },
    updated() {
      if (!this.items || this.items.length === 0) {
        return;
      }
      const baseline = Math.max(1, this.initialApplicantsCount);
      for (let i = baseline; i < this.items.length; i += 1) {
        const row = this.items[i];
        if (row && row.AddedNewApplicant !== "1") {
          row.AddedNewApplicant = "1";
        }
      }
    },
    methods: {
      sprintf,
      indexString(index, total) {
        if(this.$attrs.applicationType === 'owner-occupancy') {
          return index === 0 ? `` : `(${index + 1}/${total})`;
        } else {
          return index === 0 ? `(${this.translate('Päähakija')})` : `(${index + 1}/${total})`;
        }

      },
      // Lock SSN only for applicants loaded with an existing application (API / saved state).
      // New rows from "+ Lisää hakija" must allow entering SSN.
      ssnDisabled(index) {

        // If new application, allow always.
        if (!this.isExistingApplication) {
          return false;
        }

        // If existing applicant, lock SSN.
        if (this.existingApplicantIndexes[index]) {
          return true;
        }

        // If existing application BUT the applicant is new - allow entering SSN.
        return index < this.initialApplicantsCount;
      },
    },
  };
</script>
